import React, { useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { selectCategoryData, selectClients, selectLoading, selectSelectedClient, fetchCategoryMasters } from '../store/slices/categoryReportsSlice';
import { savecategorymasters } from '../utilities/api';
import CategoryDialog from './CategoryDialog';
import './CategoryMaster.css';

const CategorymastersContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const categoryData = useSelector(selectCategoryData);
    const clients = useSelector(selectClients);
    const loading = useSelector(selectLoading);
    const selectedClient = useSelector(selectSelectedClient);

    const [statusFilter, setStatusFilter] = useState('Active');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [editCategory, setEditCategory] = useState(null);

    const clientFilteredData = useMemo(() => {
        if (selectedClient?.client_id) {
            return categoryData.filter((item) => String(item.client_id) === String(selectedClient.client_id));
        }
        return categoryData;
    }, [categoryData, selectedClient]);

    const activeCount = useMemo(() => clientFilteredData.filter((item) => item.catmasters_status === 'Active').length, [clientFilteredData]);
    const inactiveCount = useMemo(() => clientFilteredData.filter((item) => item.catmasters_status === 'Inactive').length, [clientFilteredData]);

    const filteredData = useMemo(() => clientFilteredData.filter((item) => item.catmasters_status === statusFilter), [clientFilteredData, statusFilter]);

    const getClientName = (row) => {
        const client = clients.find((c) => String(c.client_id) === String(row.client_id));
        return client?.client_name || 'N/A';
    };

    const reload = () => {
        dispatch(
            fetchCategoryMasters({
                rolename: localStorage.getItem('role') || '',
                clientname: localStorage.getItem('clientid') || ''
            })
        );
    };

    const handleSaveRequest = (items) => {
        const isEdit = items.length === 1 && !!items[0].category_master_id;
        confirmDialog({
            message: isEdit ? 'Are you sure you want to update this category?' : `Are you sure you want to add ${items.length} new categor${items.length > 1 ? 'ies' : 'y'}?`,
            header: isEdit ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleConfirmedSave(items)
        });
    };

    const handleConfirmedSave = async (items) => {
        const payload = {
            categories: items.map((item) => ({
                category_master_id: item.category_master_id || null,
                category_name: item.category_name.trim(),
                client_id: item.client_id,
                status: item.status,
                effective_from: item.effective_from ? (item.effective_from instanceof Date ? item.effective_from.toISOString().split('T')[0] : item.effective_from) : null
            })),
            updated_by: localStorage.getItem('userid') || ''
        };
        try {
            const result = await savecategorymasters(payload);
            if (result?.status === 'success') {
                toast.current.show({ severity: 'success', summary: 'Success', detail: result.message || 'Saved successfully', life: 3000 });
                setDisplayDialog(false);
                setEditCategory(null);
                reload();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: result?.message || 'Save failed. Please try again.', life: 4000 });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Save failed. Please try again.', life: 4000 });
        }
    };

    const statusBodyTemplate = (rowData) => <Tag value={rowData.catmasters_status} severity={rowData.catmasters_status === 'Active' ? 'success' : 'danger'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />;

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={() => {
                setEditCategory(rowData);
                setDisplayDialog(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">Category List</h3>
                    <div className="category-status-tabs">
                        <button className={`status-tab-btn active-tab${statusFilter === 'Active' ? ' selected' : ''}`} onClick={() => setStatusFilter('Active')}>
                            <i className="pi pi-check-circle" />
                            Active <span className="tab-count">{activeCount}</span>
                        </button>
                        <button className={`status-tab-btn inactive-tab${statusFilter === 'Inactive' ? ' selected' : ''}`} onClick={() => setStatusFilter('Inactive')}>
                            <i className="pi pi-times-circle" />
                            Inactive <span className="tab-count">{inactiveCount}</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                                setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                            }}
                            placeholder="Search bills..."
                            className="w-full"
                        />
                    </span>
                    <Button
                        label="Add Category"
                        icon="pi pi-plus"
                        className="p-button-primary"
                        onClick={() => {
                            setEditCategory(null);
                            setDisplayDialog(true);
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="sub-masters-table-wrap">
                <DataTable
                    value={filteredData}
                    filters={filters}
                    globalFilterFields={['category_name']}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                    showGridlines
                    tableStyle={{ minWidth: '40rem' }}
                    className="p-datatable-sm sub-masters-table"
                    emptyMessage={`No ${statusFilter.toLowerCase()} categories found`}
                >
                    <Column field="category_master_id" header="Category ID" sortable style={{ width: '130px' }} />
                    <Column field="category_name" header="Category Name" sortable />
                    <Column header="Client" body={(row) => getClientName(row)} sortable sortField="client_id" />
                    <Column field="catmasters_status" header="Status" body={statusBodyTemplate} sortable style={{ width: '120px', textAlign: 'center' }} />
                    <Column header="Actions" body={actionBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                </DataTable>
                </div>
            )}

            <CategoryDialog
                visible={displayDialog}
                categoryData={editCategory}
                clients={clients}
                allCategories={categoryData}
                onHide={() => {
                    setDisplayDialog(false);
                    setEditCategory(null);
                }}
                onSave={handleSaveRequest}
            />
        </Card>
    );
};

export default CategorymastersContent;
